import { Table } from '@tanstack/react-table';
import { RefObject, useLayoutEffect, useRef } from 'react';

export function useColumnResizeIndicator<T>({
  table,
  headerRefs,
  isColumnSizingEnabled,
  tableElRef,
}: {
  table: Table<T>;
  headerRefs: RefObject<Map<string, HTMLTableCellElement>>;
  isColumnSizingEnabled: boolean;
  tableElRef: RefObject<HTMLTableElement | null>;
}) {
  const gradientLineRef = useRef<HTMLDivElement>(null);
  const resizingStartRef = useRef<{
    startX: number;
    startEdgeX: number;
    startSize: number;
    minSize: number;
    columnId: string;
  } | null>(null);

  const isResizingColumnId = table.getState().columnSizingInfo.isResizingColumn;

  useLayoutEffect(() => {
    if (!isColumnSizingEnabled || !isResizingColumnId) {
      resizingStartRef.current = null;
      return;
    }
    const colId = isResizingColumnId as string;
    const th = headerRefs.current?.get(colId);
    const startEdgeX = th ? th.offsetLeft + th.offsetWidth : 0;
    const { startOffset, startSize: sizingStartSize } = table.getState().columnSizingInfo;
    const col = table.getColumn(colId);
    const startX = startOffset ?? 0;
    const startSize = sizingStartSize ?? col?.getSize() ?? 0;
    const minSize = col?.columnDef.minSize ?? 0;
    resizingStartRef.current = { startX, startEdgeX, startSize, minSize, columnId: colId };
    if (gradientLineRef.current) {
      gradientLineRef.current.style.left = `calc(${startEdgeX}px - 0.5625rem)`;
    }
    const updatePosition = (clientX: number) => {
      const s = resizingStartRef.current;
      if (!s || !gradientLineRef.current) return;
      const newSize = Math.max(s.startSize + (clientX - s.startX), s.minSize);
      const newEdgeX = s.startEdgeX - s.startSize + newSize;
      tableElRef.current?.style.setProperty(`--col-${s.columnId}-size`, String(newSize));
      gradientLineRef.current.style.left = `calc(${newEdgeX}px - 0.5625rem)`;
    };
    const onMouseMove = (e: MouseEvent) => updatePosition(e.clientX);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) updatePosition(e.touches[0].clientX);
    };
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onTouchMove);
    return () => {
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizingColumnId, isColumnSizingEnabled]);

  return { gradientLineRef, isResizingColumnId };
}
